import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { CreateTaskData, WeeklyTaskLimits } from '../types/task'
import type { Client, Shoot } from '../types'
import FuzzyClientSearch from './FuzzyClientSearch'
import { 
  XMarkIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CameraIcon,
  TagIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface CreateTaskModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (taskData: CreateTaskData) => void
  clients: Client[]
  shoots: Shoot[]
  weeklyLimits: WeeklyTaskLimits | null
}

const CreateTaskModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  clients, 
  shoots, 
  weeklyLimits 
}: CreateTaskModalProps) => {
  const [formData, setFormData] = useState<CreateTaskData>({
    title: '',
    description: '',
    priority: 'medium',
    category: 'other',
    dueDate: '',
    estimatedMinutes: undefined,
    tags: [],
    clientId: '',
    shootId: ''
  })
  const [customCategory, setCustomCategory] = useState('')
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  
  const [tagInput, setTagInput] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        category: 'other',
        dueDate: '',
        estimatedMinutes: undefined,
        tags: [],
        clientId: '',
        shootId: ''
      })
      setTagInput('')
      setCustomCategory('')
      setSelectedClient(null)
      setErrors({})
    }
  }, [isOpen])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Task title is required'
    }

    if (formData.estimatedMinutes && formData.estimatedMinutes < 1) {
      newErrors.estimatedMinutes = 'Estimated time must be at least 1 minute'
    }

    if (formData.dueDate) {
      const dueDate = new Date(formData.dueDate)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      if (dueDate < today) {
        newErrors.dueDate = 'Due date cannot be in the past'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    // Check weekly limits
    if (weeklyLimits && !weeklyLimits.canCreateTask) {
      setErrors({ general: 'Weekly task limit reached. Upgrade to Pro for unlimited tasks.' })
      return
    }

    const taskData: CreateTaskData = {
      ...formData,
      title: formData.title.trim(),
      description: formData.description?.trim() || undefined,
      estimatedMinutes: formData.estimatedMinutes || undefined,
      clientId: formData.clientId || undefined,
      shootId: formData.shootId || undefined,
      dueDate: formData.dueDate || undefined,
      // Include custom category if "other" is selected and custom category is provided
      ...(formData.category === 'other' && customCategory.trim() && {
        tags: [...(formData.tags || []), `Category: ${customCategory.trim()}`]
      })
    }

    onSubmit(taskData)
  }

  const handleAddTag = () => {
    const tag = tagInput.trim()
    if (tag && !formData.tags?.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }))
      setTagInput('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault()
      handleAddTag()
    }
  }

  const handleClientSelect = (client: Client | null) => {
    setSelectedClient(client)
    setFormData(prev => ({ ...prev, clientId: client?.id || '' }))
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto glass-mockup rounded-2xl p-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <ClipboardDocumentListIcon className="w-6 h-6 text-blue-400" />
              <h2 className="text-xl font-semibold text-white">Create New Task</h2>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </motion.button>
          </div>

          {/* Weekly Limit Warning */}
          {weeklyLimits && !weeklyLimits.isUnlimited && weeklyLimits.currentWeekUsage >= weeklyLimits.maxTasksPerWeek * 0.8 && (
            <div className="mb-6 p-4 rounded-xl border border-orange-500/30 bg-orange-500/10">
              <div className="flex items-center space-x-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-400" />
                <div>
                  <p className="text-orange-400 font-medium text-sm">
                    {weeklyLimits.currentWeekUsage >= weeklyLimits.maxTasksPerWeek 
                      ? 'Weekly task limit reached!' 
                      : `${weeklyLimits.maxTasksPerWeek - weeklyLimits.currentWeekUsage} tasks remaining this week`
                    }
                  </p>
                  <p className="text-orange-300 text-xs">
                    Upgrade to Pro for unlimited tasks
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                <p className="text-red-400 text-sm">{errors.general}</p>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-white font-medium mb-2">
                Task Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                  errors.title ? 'border-red-500/50' : 'border-white/20'
                }`}
                placeholder="Enter task title..."
                autoFocus
              />
              {errors.title && (
                <p className="text-red-400 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-white font-medium mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors resize-none"
                placeholder="Add task description..."
                rows={3}
              />
            </div>

            {/* Priority & Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white font-medium mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <option value="low" className="bg-gray-800">Low</option>
                  <option value="medium" className="bg-gray-800">Medium</option>
                  <option value="high" className="bg-gray-800">High</option>
                  <option value="urgent" className="bg-gray-800">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <option value="shoot_prep" className="bg-gray-800">Shoot Preparation</option>
                  <option value="editing" className="bg-gray-800">Editing</option>
                  <option value="client_communication" className="bg-gray-800">Client Communication</option>
                  <option value="admin" className="bg-gray-800">Administration</option>
                  <option value="marketing" className="bg-gray-800">Marketing</option>
                  <option value="other" className="bg-gray-800">Other</option>
                </select>
                
                {/* Custom Category Input - appears when "Other" is selected */}
                {formData.category === 'other' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3"
                  >
                    <input
                      type="text"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      placeholder="Specify custom category..."
                    />
                  </motion.div>
                )}
              </div>
            </div>

            {/* Due Date & Estimated Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white font-medium mb-2">
                  <CalendarIcon className="w-4 h-4 inline mr-1" />
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  className={`input-field ${
                    errors.dueDate ? 'border-red-500/50' : ''
                  }`}
                />
                {errors.dueDate && (
                  <p className="text-red-400 text-sm mt-1">{errors.dueDate}</p>
                )}
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  <ClockIcon className="w-4 h-4 inline mr-1" />
                  Estimated Time (minutes)
                </label>
                <input
                  type="number"
                  value={formData.estimatedMinutes || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    estimatedMinutes: e.target.value ? parseInt(e.target.value) : undefined 
                  }))}
                  className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    errors.estimatedMinutes ? 'border-red-500/50' : 'border-white/20'
                  }`}
                  placeholder="e.g. 60"
                  min="1"
                />
                {errors.estimatedMinutes && (
                  <p className="text-red-400 text-sm mt-1">{errors.estimatedMinutes}</p>
                )}
              </div>
            </div>

            {/* Client & Shoot Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white font-medium mb-2">
                  <UserIcon className="w-4 h-4 inline mr-1" />
                  Client (Optional)
                </label>
                <FuzzyClientSearch
                  clients={clients}
                  selectedClient={selectedClient}
                  onClientSelect={handleClientSelect}
                  placeholder="Search for a client..."
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  <CameraIcon className="w-4 h-4 inline mr-1" />
                  Shoot (Optional)
                </label>
                <select
                  value={formData.shootId}
                  onChange={(e) => setFormData(prev => ({ ...prev, shootId: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <option value="" className="bg-gray-800">No shoot</option>
                  {shoots.map(shoot => (
                    <option key={shoot.id} value={shoot.id} className="bg-gray-800">
                      {shoot.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-white font-medium mb-2">
                <TagIcon className="w-4 h-4 inline mr-1" />
                Tags
              </label>
              <div className="space-y-3">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                    placeholder="Add a tag..."
                  />
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                    className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 disabled:bg-gray-500/20 text-blue-400 disabled:text-gray-500 rounded-xl transition-colors"
                  >
                    Add
                  </motion.button>
                </div>
                
                {formData.tags && formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <motion.span
                        key={index}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center space-x-1 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm"
                      >
                        <span>{tag}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-red-400 transition-colors"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </motion.span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-4 pt-4">
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 rounded-xl transition-colors"
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={weeklyLimits ? !weeklyLimits.canCreateTask : false}
                className={`flex-1 px-6 py-3 rounded-xl transition-colors ${
                  weeklyLimits && !weeklyLimits.canCreateTask
                    ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
                }`}
              >
                Create Task
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default CreateTaskModal 